/**
 * Shared third-party identity links row (LinkedIn / ORCID / AGU Connect),
 * extracted from the result card so the experience drawer shows the same
 * links (client ask 2026-08-12). Absence of a link is never surfaced as a
 * gap — the whole row hides when the candidate carries none.
 */
import { Component, Input } from '@angular/core';
import type { MapsCandidate } from '../../models/maps.types';

@Component({
    selector: 'maps-profile-links',
    standalone: false,
    template: `
        <div class="profile-links" *ngIf="linkedInHref || orcidHref || aguProfileHref">
            <a
                *ngIf="linkedInHref"
                class="profile-link"
                [href]="linkedInHref"
                target="_blank"
                rel="noopener">
                <span class="glyph linkedin" aria-hidden="true">in</span> LinkedIn
            </a>
            <a
                *ngIf="orcidHref"
                class="profile-link"
                [href]="orcidHref"
                target="_blank"
                rel="noopener">
                <span class="glyph orcid" aria-hidden="true">iD</span> ORCID
            </a>
            <a
                *ngIf="aguProfileHref"
                class="profile-link"
                data-testid="agu-profile-link"
                [href]="aguProfileHref"
                target="_blank"
                rel="noopener">
                <span class="glyph agu" aria-hidden="true">
                    <!-- Official AGU simple wordmark (white variant, client-supplied 8-12). -->
                    <svg viewBox="0 0 94 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.6673 17.5298L16.376 9.04715L18.825 16.1989C21.0514 15.6709 23.2983 15.1953 25.5617 14.7764L20.1856 0.712891H12.4635L5.42578 20.1042C8.13861 19.1617 10.8885 18.3021 13.6673 17.5298Z" fill="white"/>
                        <path d="M20.0246 19.7035L14.0754 21.1959C10.6163 22.1471 7.20673 23.2336 3.85486 24.4423L0 35.0674H8.04366L10.5751 27.0647L22.1768 26.0568L25.2565 35.0674H33.3332L26.9139 18.3203C24.5969 18.7218 22.3005 19.1843 20.0246 19.7035Z" fill="white"/>
                        <path d="M40.0121 12.8995C41.4469 9.87997 44.3329 7.89895 48.4763 7.85967C51.6757 7.82913 54.2813 8.97673 56.8004 10.9665L61.4303 4.39943C57.93 1.58498 53.836 -0.0469684 48.5959 0.00102999C39.0639 0.0926633 32.8425 5.50339 31.3789 13.8333C34.2319 13.4362 37.1097 13.1177 40.0121 12.8995Z" fill="white"/>
                        <path d="M50.4834 16.2758C49.7125 16.2758 48.9456 16.2933 48.1746 16.3064L48.2241 22.0357L56.544 22.1404C56.1977 24.9417 54.0703 27.922 48.8302 27.97C42.5676 28.0311 39.0096 23.7985 38.9601 17.9602C38.9601 17.55 38.9766 17.1529 39.0096 16.7558C36.3339 16.9784 33.6787 17.2838 31.0442 17.6591C31.0442 17.7682 31.0319 17.8685 31.036 17.9776C31.1349 29.17 38.3788 35.9203 48.389 35.8243C59.2445 35.7196 64.1878 27.682 64.13 16.9478C59.6361 16.5027 55.0845 16.2715 50.4834 16.2715" fill="white"/>
                        <path d="M93.7755 19.5021V0.712891H86.1523V17.2156C88.7209 17.905 91.2647 18.6686 93.7755 19.5021Z" fill="white"/>
                        <path d="M86.1715 20.9996C86.0644 25.0838 84.0153 27.9462 80.078 27.9855C75.9964 28.0248 73.8855 24.9965 73.8484 20.681L73.8278 18.2768C71.2963 17.8404 68.6041 17.4608 66.0273 17.1641V21.4054C66.1016 30.2851 70.9583 35.9183 80.144 35.8311C88.5381 35.7525 93.0814 30.9309 93.704 23.2555C91.222 22.4308 88.7071 21.6803 86.1715 20.9952" fill="white"/>
                        <path d="M73.8275 14.4928V0.712891H66.0312V13.3845C68.6039 13.6812 71.2961 14.0564 73.8275 14.4928Z" fill="white"/>
                    </svg>
                </span> AGU Profile
            </a>
        </div>
    `,
    styleUrl: './profile-links.component.scss',
})
export class ProfileLinksComponent {
    @Input({ required: true }) candidate!: MapsCandidate;

    get linkedInHref(): string | null {
        return this.candidate.linkedInUrl ?? null;
    }

    /** AGU Connect profile page (Higher Logic ?UserKey= pattern) — must ONLY
     *  ever be the AGU profile URL, never confex.com (6-16 finding 3 / G9). */
    get aguProfileHref(): string | null {
        return this.candidate.aguProfileUrl ?? null;
    }

    /** ORCID profile URL. The feed may supply either a bare iD
     *  (0000-0002-1825-0097) or a full URL; a bare iD is normalized to
     *  https://orcid.org/<id>. Null when not provided. */
    get orcidHref(): string | null {
        const raw = this.candidate.orcid?.trim();
        if (!raw) return null;
        return /^https?:\/\//i.test(raw) ? raw : `https://orcid.org/${raw}`;
    }
}
